/**
 * legacyStyleToGeostyler — one-way migration helpers from the legacy MapLibre
 * shorthand styling to a stored GeoStyler {@link Style} (the new source of truth).
 *
 * Two inputs are converted:
 *  1. CM-table `map_display_config.style` — a `["match", ["get", key], …]` that
 *     maps a styling value → icon/colour (point→icon, line→line-color,
 *     polygon→fill-color). See {@link tableStyleToGeostyler}.
 *  2. `config.json` map-layer shorthand — `defaultIcon` + `style` (single icon or
 *     line/fill colour, plus label props). See {@link layerShorthandToGeostyler}.
 *
 * Output contract (so the existing `geostylerToMapLibre` converter renders it with
 * no special-casing):
 *  - Each choice becomes a rule filtered `["==", filterProperty, value]`.
 *  - The default output becomes a rule filtered `["!", ["||", …every value]]`, so
 *    every feature matches exactly one rule (no double-draw for the fallback).
 *  - Point labels become one no-filter `Text` rule (rendered for all features).
 *  - `filterProperty` defaults to `"style_value"` — the GeoJSON property the
 *    backend serializer exposes from each row's `map_display_config[styleKey]`.
 */
import type { Filter, IconSymbolizer, LineSymbolizer, Rule, Style, TextSymbolizer } from "geostyler-style";
import { baseGeometry, getStyleChoices, getStyleDefaultOutput, type CmSpatialType } from "./cmTableStyle";
import { toShippedSharedIcon } from "./sharedIcons";

export interface LegacyToGeostylerOptions {
  /** GeoJSON property the rules filter on (backend sets it = `map_display_config[styleKey]`). */
  filterProperty?: string;
  /** CDN-relative base the icon paths are rewritten under (e.g. `public-icons` or `public-icons/imw`). */
  iconBasePath?: string;
  /** `Style.name`. */
  name?: string;
}

const DEFAULT_FILTER_PROPERTY = "style_value";
const DEFAULT_ICON_BASE = "public-icons/imw";

/**
 * `icons/Camp.png` (or `Camp.png`) → `${base}/Camp.png`, or the app's own copy
 * (`icons/imw/Camp.png`) when the shared library ships that icon; absolute/data
 * URLs pass through.
 */
export function rewriteIconPath(path: string, base: string = DEFAULT_ICON_BASE): string {
  if (!path) return "";
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  const clean = path.replace(/^\/+/, "").replace(/^icons\//i, "");
  return toShippedSharedIcon(`${base.replace(/\/+$/, "")}/${clean}`);
}

/** MapLibre `{name}` label token → GeoStyler `{{name}}` template. */
export function mapLibreLabelToGeostyler(label: unknown): string | undefined {
  if (typeof label !== "string") return undefined;
  return label.replace(/\{\s*([^}]+?)\s*\}/g, "{{$1}}");
}

const eq = (prop: string, value: string): Filter => ["==", prop, value] as unknown as Filter;

/** `["!", ["||", ==v1, ==v2, …]]` — matches features whose value is none of `values`. */
function notAnyOf(prop: string, values: string[]): Filter | undefined {
  if (!values.length) return undefined;
  return ["!", ["||", ...values.map((v) => eq(prop, v))]] as unknown as Filter;
}

function makeIcon(image: string, size: unknown): IconSymbolizer {
  const s: Record<string, unknown> = { kind: "Icon", image };
  if (size !== undefined) s.size = size;
  return s as unknown as IconSymbolizer;
}

function makeLine(color: string, width: unknown): LineSymbolizer {
  const s: Record<string, unknown> = { kind: "Line", color };
  if (width !== undefined) s.width = width;
  return s as unknown as LineSymbolizer;
}

function makeFill(color: string, opacity: unknown): Record<string, unknown> {
  const s: Record<string, unknown> = { kind: "Fill", color };
  if (opacity !== undefined) s.opacity = opacity;
  return s;
}

/** Builds a `Text` symbolizer from the legacy `label*` style props. */
function makeText(style: Record<string, any>): TextSymbolizer {
  const t: Record<string, unknown> = { kind: "Text", label: mapLibreLabelToGeostyler(style.labelField) };
  if (style.labelFont !== undefined) t.font = style.labelFont;
  if (style.labelSize !== undefined) t.size = style.labelSize;
  if (style.labelColor !== undefined) t.color = style.labelColor;
  if (style.labelOffset !== undefined) t.offset = style.labelOffset;
  if (style.labelOpacity !== undefined) t.opacity = style.labelOpacity;
  if (style.labelHaloColor !== undefined) t.haloColor = style.labelHaloColor;
  if (style.labelHaloWidth !== undefined) t.haloWidth = style.labelHaloWidth;
  return t as unknown as TextSymbolizer;
}

/**
 * Converts a CM-table `map_display_config.style` (match shorthand) into a stored
 * GeoStyler {@link Style}. Zoom-dependent values (`iconSize`, `labelSize`, …) are
 * carried through verbatim — both web and RN render MapLibre-dialect expressions.
 */
export function tableStyleToGeostyler(
  mdcStyle: Record<string, any> | undefined,
  spatialType: CmSpatialType | string | null | undefined,
  options: LegacyToGeostylerOptions = {}
): Style {
  // Migrated styles are already the canonical runtime/export representation.
  // Rebuilding them from choices would drop rule-level styling details.
  if (mdcStyle && Array.isArray(mdcStyle.rules)) return mdcStyle as Style;

  const filterProp = options.filterProperty ?? DEFAULT_FILTER_PROPERTY;
  const iconBase = options.iconBasePath ?? DEFAULT_ICON_BASE;
  const geom = baseGeometry(spatialType);
  const choices = getStyleChoices(mdcStyle, spatialType);
  const defaultOutput = getStyleDefaultOutput(mdcStyle, spatialType);
  const defaultFilter = notAnyOf(
    filterProp,
    choices.map((c) => c.value)
  );
  const rules: Rule[] = [];

  if (geom === "point") {
    const iconSize = mdcStyle?.iconSize;
    for (const c of choices) {
      rules.push({
        name: c.value,
        filter: eq(filterProp, c.value),
        symbolizers: [makeIcon(rewriteIconPath(c.icon ?? "", iconBase), iconSize)]
      });
    }
    if (defaultOutput) {
      rules.push({
        name: "__default",
        ...(defaultFilter ? { filter: defaultFilter } : {}),
        symbolizers: [makeIcon(rewriteIconPath(defaultOutput, iconBase), iconSize)]
      });
    }
    // Only emit a label rule when labelField is a string template that
    // mapLibreLabelToGeostyler can convert. MapLibre expression arrays (e.g.
    // ["get","name"]) are NOT strings and produce label:undefined, which makes
    // the symbol layer invisible.
    if (mdcStyle?.labelField) {
      const labelTemplate = mapLibreLabelToGeostyler(mdcStyle.labelField);
      if (labelTemplate !== undefined) {
        rules.push({ name: "__label", symbolizers: [makeText(mdcStyle)] });
      }
    }
  } else if (geom === "linestring") {
    const width = mdcStyle?.["line-width"];
    for (const c of choices) {
      rules.push({ name: c.value, filter: eq(filterProp, c.value), symbolizers: [makeLine(c.color ?? "", width)] });
    }
    if (defaultOutput) {
      rules.push({ name: "__default", ...(defaultFilter ? { filter: defaultFilter } : {}), symbolizers: [makeLine(defaultOutput, width)] });
    }
  } else if (geom === "polygon") {
    const opacity = mdcStyle?.["fill-opacity"];
    for (const c of choices) {
      rules.push({
        name: c.value,
        filter: eq(filterProp, c.value),
        symbolizers: [makeFill(c.color ?? "", opacity) as unknown as Rule["symbolizers"][number]]
      });
    }
    if (defaultOutput) {
      rules.push({
        name: "__default",
        ...(defaultFilter ? { filter: defaultFilter } : {}),
        symbolizers: [makeFill(defaultOutput, opacity) as unknown as Rule["symbolizers"][number]]
      });
    }
  }

  return { name: options.name ?? "style", rules };
}
